import React, { useState, useCallback, useEffect } from 'react';
import { useAddin } from '../App';
import Modal from './Modal';
import * as webhook from '../services/webhook';

export default function FileEmailModal({ isOpen, onClose, onSuccess }) {
    const { linkedMatter, itemId, conversationId, userEmail, senderInfo } = useAddin();
    const [loading, setLoading] = useState(true);
    const [filing, setFiling] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [emails, setEmails] = useState([]);
    const [selectedIds, setSelectedIds] = useState(new Set());

    // Toggles
    const [extractAttachments, setExtractAttachments] = useState(true);
    const [extractImages, setExtractImages] = useState(false);

    // Fetch conversation emails
    useEffect(() => {
        if (!isOpen || !conversationId) return;

        let isMounted = true;
        setLoading(true);
        setFeedback(null);
        setSelectedIds(new Set());

        webhook.getConversationEmails({ conversationId, userEmail })
            .then(result => {
                if (!isMounted) return;

                // Expecting array of { id, subject, sender, date, is_filed }
                const list = result.emails || [];
                setEmails(list);

                // By default, select emails that are NOT filed and NOT already in the Filed folder
                const unfiled = new Set(list.filter(e => !e.is_filed && !e.in_filed_folder).map(e => e.id));
                // If it's a completely new thread, or didn't return a structured list, select current item
                if (unfiled.size === 0 && list.length === 0 && itemId) {
                    unfiled.add(itemId);
                }
                setSelectedIds(unfiled);
            })
            .catch(err => {
                if (!isMounted) return;
                console.error("Failed to load conversation emails:", err);
                // Fallback to just the current item
                setEmails([{ id: itemId, subject: 'Current Item', sender: senderInfo?.name || senderInfo?.email, date: new Date().toISOString(), is_filed: false }]);
                setSelectedIds(new Set([itemId]));
            })
            .finally(() => {
                if (isMounted) setLoading(false);
            });

        return () => { isMounted = false; };
    }, [isOpen, conversationId, userEmail, itemId, senderInfo]);

    const handleToggleEmail = (id) => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }
        setSelectedIds(next);
    };

    const handleSelectAll = () => {
        const allIds = new Set(emails.map(e => e.id));
        setSelectedIds(allIds);
    };

    const handleDeselectAll = () => {
        setSelectedIds(new Set());
    };

    const handleSubmit = useCallback(async () => {
        if (selectedIds.size === 0) {
            setFeedback({ type: 'error', message: 'Select at least one email to file' });
            return;
        }

        setFiling(true);
        setFeedback(null);

        try {
            await webhook.fileEmail({
                source: 'reading_pane',
                outlookItemIds: Array.from(selectedIds),
                conversationId,
                userEmail,
                matterId: linkedMatter?.id || linkedMatter?.matter_id,
                matterNumber: linkedMatter?.number || linkedMatter?.matter_number,
                matterDescription: linkedMatter?.description || linkedMatter?.matter_description || '',
                clientName: linkedMatter?.client?.name || linkedMatter?.client_name || linkedMatter?.clientName || 'Unknown Client',
                contactId: linkedMatter?.client?.id || null,
                extractAttachments,
                extractImages
            });

            setFeedback({ type: 'success', message: 'Emails successfully queued for filing' });
            setTimeout(() => {
                setFeedback(null);
                onClose();
                if (onSuccess) onSuccess();
            }, 1500);
        } catch (err) {
            setFeedback({ type: 'error', message: err.message || 'Failed to file emails' });
        } finally {
            setFiling(false);
        }
    }, [selectedIds, conversationId, userEmail, linkedMatter, extractAttachments, extractImages, onClose, onSuccess]);

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="File Email">
            {/* Matter context */}
            {linkedMatter && (
                <div className="info-block" style={{ padding: '8px 11px', marginBottom: '12px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--navy)' }}>
                        {linkedMatter.name || linkedMatter.matter_description}
                    </div>
                    <div style={{ fontSize: '9px', color: 'var(--grey02)' }}>
                        {linkedMatter.number || linkedMatter.matter_number} • {linkedMatter.client?.name || linkedMatter.client_name || linkedMatter.clientName || 'Unknown Client'} ({linkedMatter.status || 'Open'})
                    </div>
                </div>
            )}

            <div className="section-label" style={{ marginBottom: '6px' }}>Select Emails</div>
            <div className="info-block email-list-container" style={{ maxHeight: '160px', overflowY: 'auto', marginBottom: '12px' }}>
                {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center', fontSize: '10px', color: 'var(--grey02)' }}>
                        Loading thread...
                    </div>
                ) : emails.length === 0 ? (
                    <div style={{ padding: '12px 11px', fontSize: '10px', color: 'var(--grey02)' }}>
                        <label className="checkbox-wrap">
                            <input
                                type="checkbox"
                                checked={selectedIds.has(itemId)}
                                onChange={() => handleToggleEmail(itemId)}
                            />
                            <span>Current selection only</span>
                        </label>
                    </div>
                ) : (
                    <div className="email-list">
                        {emails.map((email, idx) => (
                            <div key={email.id || idx} className={`email-list-item ${email.is_filed ? 'filed' : ''}`} style={{ padding: '8px 11px', borderBottom: '1px solid var(--grey04)' }}>
                                <label className="checkbox-wrap" style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={selectedIds.has(email.id)}
                                        onChange={() => handleToggleEmail(email.id)}
                                        style={{ marginTop: '2px' }}
                                    />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--navy)', display: 'flex', justifyContent: 'space-between', gap: '4px' }}>
                                            <span className="truncate" style={{ maxWidth: '120px' }}>{email.sender}</span>
                                            <span style={{ display: 'flex', gap: '3px' }}>
                                                {email.is_filed && <span style={{ fontSize: '8px', color: 'var(--teal)', padding: '1px 4px', background: 'var(--light-teal)', borderRadius: '4px' }}>Filed</span>}
                                                {email.in_filed_folder && !email.is_filed && <span style={{ fontSize: '8px', color: 'var(--grey02)', padding: '1px 4px', background: 'var(--grey04)', borderRadius: '4px' }}>Moved</span>}
                                            </span>
                                        </div>
                                        <div style={{ fontSize: '9px', color: 'var(--grey02)', display: 'flex', justifyContent: 'space-between' }}>
                                            <span className="truncate" style={{ maxWidth: '120px' }}>{email.subject}</span>
                                            <span>{email.date ? new Date(email.date).toLocaleDateString() : ''}</span>
                                        </div>
                                    </div>
                                </label>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {!loading && emails.length > 0 && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginBottom: '12px', padding: '0 4px' }}>
                    <button onClick={handleSelectAll} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontSize: '9px', fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase' }}>Select All</button>
                    <button onClick={handleDeselectAll} style={{ background: 'none', border: 'none', color: 'var(--grey02)', fontSize: '9px', fontWeight: 700, cursor: 'pointer', textTransform: 'uppercase' }}>None</button>
                </div>
            )}

            <div className="section-label" style={{ marginBottom: '6px' }}>Options</div>
            <div className="info-block" style={{ padding: '8px 11px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label className="checkbox-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={extractAttachments}
                        onChange={(e) => setExtractAttachments(e.target.checked)}
                    />
                    <span style={{ fontSize: '10px', color: 'var(--navy)' }}>Extract Attachments</span>
                </label>
                <label className="checkbox-wrap" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={extractImages}
                        onChange={(e) => setExtractImages(e.target.checked)}
                    />
                    <span style={{ fontSize: '10px', color: 'var(--navy)' }}>Extract Embedded Images</span>
                </label>
            </div>

            {/* Feedback */}
            {feedback && (
                <div className={`modal-feedback ${feedback.type}`} style={{ marginTop: '12px' }}>
                    {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
                </div>
            )}

            <button
                className="modal-submit"
                onClick={handleSubmit}
                disabled={filing || selectedIds.size === 0}
                style={{ marginTop: '16px' }}
            >
                {filing && <div className="btn-spinner" />}
                {filing ? 'Filing...' : `File ${selectedIds.size} Email${selectedIds.size !== 1 ? 's' : ''}`}
            </button>
        </Modal>
    );
}
